import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "soap-bubbles";

const DEFAULT_BG = "#0d1b2e";
const DEFAULT_RIM_A = "#67e8f9";
const DEFAULT_RIM_B = "#f9a8d4";
const DEFAULT_HIGHLIGHT = "#ffffff";

const BUBBLE_DENSITY = 0.00004;   // bubbles per pixel² of canvas
const MIN_BUBBLES = 8;
const MAX_BUBBLES = 38;
const POP_DURATION_MS = 420;      // pop animation length

function rand(min, max) { return min + Math.random() * (max - min); }

export default function SoapBubbles({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_RIM_A, DEFAULT_RIM_B, DEFAULT_HIGHLIGHT]);
  const bubblesRef = useRef([]);
  const popsRef = useRef([]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_RIM_A),
      pickColor(colors, 2, DEFAULT_RIM_B),
      pickColor(colors, 3, DEFAULT_HIGHLIGHT),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let t = 0;

    // A bubble: position, radius, vertical rise speed, sinusoidal sway
    // (so it doesn't rise in a perfectly straight line), and a random
    // pop chance per frame so old bubbles don't accumulate.
    const newBubble = (initial) => ({
      x: rand(0, width),
      y: initial ? rand(0, height) : height + rand(20, 200),
      radius: rand(14, 64),
      vy: rand(0.18, 0.55),
      swayPhase: rand(0, Math.PI * 2),
      swayPeriod: rand(0.4, 1.0),
      swayAmp: rand(8, 26),
      hueOffset: Math.random(),    // 0..1 — selects rim color blend per bubble
    });

    const seedBubbles = () => {
      const target = Math.round(width * height * BUBBLE_DENSITY);
      const count = Math.max(MIN_BUBBLES, Math.min(MAX_BUBBLES, target));
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) {
        arr[i] = newBubble(true);
      }
      bubblesRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedBubbles();
    };

    const drawBubble = (b, alpha = 1) => {
      const [, rimA, rimB, hi] = colorsRef.current;

      // Faintly tinted interior so the bubble has volume but stays mostly
      // see-through. Uses a radial gradient that's near-zero at center and
      // a bit stronger near the edge → reads as a thin film.
      const fill = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.radius);
      fill.addColorStop(0, rgba(rimA, 0.02 * alpha));
      fill.addColorStop(0.85, rgba(rimB, 0.05 * alpha));
      fill.addColorStop(1, rgba(rimA, 0.16 * alpha));
      ctx.fillStyle = fill;
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
      ctx.fill();

      // Iridescent rim — the signature look. We draw the same circle as a
      // stroke with a conic-style gradient approximated by stroking three
      // arcs in three different colors.
      ctx.lineWidth = 1.6;
      const arcOffset = (t * 0.7 + b.hueOffset * Math.PI * 2) % (Math.PI * 2);

      const arcs = [
        { color: rimA, start: arcOffset, end: arcOffset + Math.PI * 0.7, alpha: 0.85 * alpha },
        { color: rimB, start: arcOffset + Math.PI * 0.7, end: arcOffset + Math.PI * 1.3, alpha: 0.7 * alpha },
        { color: hi,   start: arcOffset + Math.PI * 1.3, end: arcOffset + Math.PI * 2,   alpha: 0.5 * alpha },
      ];
      for (let i = 0; i < arcs.length; i += 1) {
        const a = arcs[i];
        ctx.strokeStyle = rgba(a.color, a.alpha);
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.radius, a.start, a.end);
        ctx.stroke();
      }

      // Specular highlight — a small bright crescent in the upper-left.
      // Sells the "glassy sphere" feel.
      const hx = b.x - b.radius * 0.35;
      const hy = b.y - b.radius * 0.40;
      const hr = b.radius * 0.28;
      const sg = ctx.createRadialGradient(hx, hy, 0, hx, hy, hr);
      sg.addColorStop(0, rgba(hi, 0.55 * alpha));
      sg.addColorStop(1, rgba(hi, 0));
      ctx.fillStyle = sg;
      ctx.beginPath();
      ctx.arc(hx, hy, hr, 0, Math.PI * 2);
      ctx.fill();
    };

    const tick = (ts) => {
      const [bg, rimA] = colorsRef.current;

      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, bg);
      grad.addColorStop(1, rgba(bg, 0.85));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      const bubbles = bubblesRef.current;
      const pops = popsRef.current;

      for (let i = bubbles.length - 1; i >= 0; i -= 1) {
        const b = bubbles[i];
        b.y -= b.vy;
        b.x += Math.sin(t * b.swayPeriod + b.swayPhase) * 0.4;

        // Random pop chance — older + higher bubbles are more likely to pop.
        const popChance = 0.0007 + Math.max(0, 1 - b.y / height) * 0.0008;
        if (Math.random() < popChance || b.y < -b.radius - 20) {
          if (b.y > -b.radius - 20) {
            pops.push({ x: b.x, y: b.y, radius: b.radius, born: ts });
          }
          bubbles[i] = newBubble(false);
          continue;
        }

        drawBubble(b, 1);
      }

      // Render pops on top — expanding shockwave ring that fades.
      for (let i = pops.length - 1; i >= 0; i -= 1) {
        const p = pops[i];
        const age = ts - p.born;
        if (age > POP_DURATION_MS) {
          pops.splice(i, 1);
          continue;
        }
        const k = age / POP_DURATION_MS;       // 0..1
        const r = p.radius * (1 + k * 0.6);
        const alpha = (1 - k) * 0.7;
        ctx.lineWidth = 2;
        ctx.strokeStyle = rgba(rimA, alpha);
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.stroke();
      }

      t += 0.016;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
